import type { StructureTreeOut } from '@/lib/api/generated/types.ts/structureTreeOut'
import * as z from 'zod'

export const nodeKindSchema = z.enum(['material', 'part', 'assembly'])
export type NodeKind = z.infer<typeof nodeKindSchema>

export const sheetSearchSchema = z.object({
  node: z.string().optional(),
  kind: nodeKindSchema.optional(),
})

/**
 * Search schema for the global Nomenclature page (`/nomenclature/`).
 *
 * `node` encodes either an existing selection (`mat:5`, `part:7`, `asm:5`,
 * `group:7`) or a create flow (`new`).
 *
 * Disambiguators:
 *   - `project` — required for `part:N` / `asm:N` (Part.id is global-unique
 *     so technically optional, but we always include it for clearer DX and
 *     so the inspector knows which project's structure_tree to consult).
 *   - `group` — preselect a folder when starting a create flow.
 *   - `kind` — what kind of entity the `new` flow is for.
 *   - `mode=edit` — switches an existing entity to inline edit form.
 */
export const globalSheetSearchSchema = z.object({
  node: z.string().optional(),
  kind: nodeKindSchema.optional(),
  project: z.coerce.number().optional(),
  group: z.coerce.number().optional(),
  mode: z.enum(['view', 'edit']).optional(),
})

/**
 * Selection model used inside the global page's inspector. Differs from
 * `SelectedNodeRef` in two ways: (a) `kind` is a wider union that includes
 * `'group'`, (b) `projectId` is carried for project-scoped entities so the
 * inspector can fetch the correct row (Part / Assembly are project-scoped
 * but their ids are NOT in the URL of the per-project endpoint without a
 * project segment).
 */
export type GlobalSelectedKind = 'material' | 'part' | 'assembly' | 'group'

export interface GlobalSelectedRef {
  kind: GlobalSelectedKind
  entityId: number
  /** Required for `part`/`assembly`/`group` (when scoped); null for global Material/global folder. */
  projectId: number | null
}

export interface GlobalSelectedNew {
  kind: 'new'
  preselectKind: NodeKind
  projectId: number | null
  groupId: number | null
}

export type GlobalSelected = GlobalSelectedRef | GlobalSelectedNew | null

const GLOBAL_KEY_PATTERN = /^(asm|part|mat|group):(\d+)$/

const GLOBAL_KIND_BY_PREFIX = {
  asm: 'assembly',
  part: 'part',
  mat: 'material',
  group: 'group',
} as const satisfies Record<string, GlobalSelectedKind>

type GlobalKeyPrefix = keyof typeof GLOBAL_KIND_BY_PREFIX

const GLOBAL_PREFIX_BY_KIND: Record<GlobalSelectedKind, GlobalKeyPrefix> = {
  assembly: 'asm',
  part: 'part',
  material: 'mat',
  group: 'group',
}

export function parseGlobalNodeKey(
  key: string,
): { kind: GlobalSelectedKind; entityId: number } | { kind: 'new' } | null {
  if (key === 'new') return { kind: 'new' }
  const m = key.match(GLOBAL_KEY_PATTERN)
  if (!m) return null
  const prefix = m[1] as GlobalKeyPrefix
  return { kind: GLOBAL_KIND_BY_PREFIX[prefix], entityId: Number(m[2]) }
}

export function makeGlobalNodeKey(kind: GlobalSelectedKind, entityId: number): string {
  return `${GLOBAL_PREFIX_BY_KIND[kind]}:${entityId}`
}

export type SheetMode = 'closed' | 'view' | 'edit' | 'create'

export interface SelectedNodeRef {
  kind: NodeKind
  entityId: number
}

const NODE_KEY_PATTERN = /^(asm|part|mat):(\d+)$/

const KIND_BY_PREFIX = {
  asm: 'assembly',
  part: 'part',
  mat: 'material',
} as const satisfies Record<string, NodeKind>

type NodeKeyPrefix = keyof typeof KIND_BY_PREFIX

export function parseNodeKey(key: string): SelectedNodeRef | { kind: 'new' } | null {
  if (key === 'new') return { kind: 'new' }
  const m = key.match(NODE_KEY_PATTERN)
  if (!m) return null
  const prefix = m[1] as NodeKeyPrefix
  const entityId = Number(m[2])
  return { kind: KIND_BY_PREFIX[prefix], entityId }
}

export function makeNodeKey(ref: SelectedNodeRef): string {
  const prefix: Record<NodeKind, string> = {
    assembly: 'asm',
    part: 'part',
    material: 'mat',
  }
  return `${prefix[ref.kind]}:${ref.entityId}`
}

export type OptimisticAction = {
  type: 'move-component'
  componentId: number
  newParentAssemblyId: number
}
